import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { VpnKey } from "@mui/icons-material";
import AppDialog from "components/common/AppDialog";
import { useGenerateEncryptedSftpKeyPairMutation } from "features/sftp/sftp";
import {
  KEY_ALGORITHM_OPTIONS,
  type KeyAlgorithm,
  downloadSshKeyBundleAsZip,
} from "./sshKeyGen";
import { errorDetailFromUnknown } from "./sftpKeyPairApiErrors";

const MIN_PASSPHRASE_LEN = 8;

interface HelperGenerateKeyPairDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const DEFAULT_PREFIX = "sftp_key";

export default function HelperGenerateKeyPairDialog({
  open,
  onClose,
  onSuccess,
  onError,
}: HelperGenerateKeyPairDialogProps) {
  const [selected, setSelected] = useState<KeyAlgorithm>("rsa-4096");
  const [filePrefix, setFilePrefix] = useState(DEFAULT_PREFIX);
  const [passphrase, setPassphrase] = useState("");
  const [confirmPassphrase, setConfirmPassphrase] = useState("");

  const [generatePair, { isLoading: isGenerating }] =
    useGenerateEncryptedSftpKeyPairMutation();

  useEffect(() => {
    if (!open) {
      setSelected("rsa-4096");
      setFilePrefix(DEFAULT_PREFIX);
      setPassphrase("");
      setConfirmPassphrase("");
    }
  }, [open]);

  const handleGenerate = async () => {
    const p = passphrase;
    const c = confirmPassphrase;
    if (p !== c) {
      onError?.("Passphrases do not match.");
      return;
    }
    if (p.length < MIN_PASSPHRASE_LEN) {
      onError?.(
        `Passphrase must be at least ${MIN_PASSPHRASE_LEN} characters.`,
      );
      return;
    }

    try {
      const data = await generatePair({
        algorithm: selected === "ed25519" ? "ed25519" : "rsa-4096",
        passphrase: p,
      }).unwrap();
      const prefix = filePrefix.trim() || DEFAULT_PREFIX;
      try {
        await downloadSshKeyBundleAsZip(
          data.private_key,
          data.public_key.trim(),
          prefix,
          selected,
          p,
        );
      } catch {
        onError?.(
          "Keys were generated but the ZIP archive could not be created.",
        );
        return;
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      onError?.(
        errorDetailFromUnknown(e) ?? "Failed to generate encrypted key pair.",
      );
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={isGenerating ? () => {} : onClose}
      title="Helper: Generate key pair"
      maxWidth="sm"
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={isGenerating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isGenerating}
            startIcon={
              isGenerating ? <CircularProgress size={16} /> : <VpnKey />
            }
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {isGenerating ? "Generating…" : "Generate & download"}
          </Button>
        </>
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        The server generates an OpenSSH-format private key encrypted with your
        passphrase. You get one ZIP containing the private key, the .pub file,
        and a passphrase reminder .txt.
      </Alert>

      <TextField
        label="Download file name prefix"
        value={filePrefix}
        onChange={(e) => setFilePrefix(e.target.value)}
        fullWidth
        size="small"
        disabled={isGenerating}
        placeholder={DEFAULT_PREFIX}
        helperText="One ZIP download: your prefix plus _ssh_keys.zip, containing private key, .pub, and passphrase.txt."
        sx={{ mb: 2 }}
        autoComplete="off"
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Algorithm
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
        {KEY_ALGORITHM_OPTIONS.map((opt) => {
          const active = selected === opt.id;
          return (
            <Box
              key={opt.id}
              onClick={() => !isGenerating && setSelected(opt.id)}
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: "2px solid",
                borderColor: active ? "primary.main" : "divider",
                bgcolor: active ? "rgba(31,93,107,0.05)" : "transparent",
                cursor: isGenerating ? "default" : "pointer",
                transition: "all 0.15s",
                "&:hover": isGenerating
                  ? {}
                  : {
                      borderColor: "primary.main",
                      bgcolor: "rgba(31,93,107,0.03)",
                    },
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {opt.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {opt.description}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <TextField
        label="Passphrase (encrypts private key)"
        type="password"
        value={passphrase}
        onChange={(e) => setPassphrase(e.target.value)}
        fullWidth
        size="small"
        disabled={isGenerating}
        autoComplete="new-password"
        helperText={`At least ${MIN_PASSPHRASE_LEN} characters (server requirement).`}
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Confirm passphrase"
        type="password"
        value={confirmPassphrase}
        onChange={(e) => setConfirmPassphrase(e.target.value)}
        fullWidth
        size="small"
        disabled={isGenerating}
        autoComplete="new-password"
      />
    </AppDialog>
  );
}
